import { useEffect } from 'react'

import { useAuth } from '@clerk/nextjs'
import { setUser as setSentryUser } from '@sentry/nextjs'
import { differenceInMinutes } from 'date-fns/fp'
import { useQuery } from 'urql'

import useLogout from 'domains/user/use-logout'
import { ViewerDocument } from 'graphql/schema/user'
import { rudder } from 'lib/analytics/rudder'
import { contactEmail } from 'lib/constants/grafbase'
import { saveCookieChoice } from 'lib/utils/cookies'
import { isProduction } from 'lib/utils/environments'
import { openInNewTab } from 'lib/utils/links'
import { toasty } from 'store/toasts.state'

import { FCC } from 'types/react'

const signUpTrackedKey = 'sign-up-tracked'

/**
 * Loads viewer (And identifies on different platform) at top website level and handles null/error to logout or retry
 */
const LoadUser: FCC = ({ children }) => {
  const { isSignedIn } = useAuth()
  const { logout } = useLogout()
  const pause = !isSignedIn
  const [{ fetching, error, data }] = useQuery({
    query: ViewerDocument,
    pause,
  })

  useEffect(() => {
    if (!data?.viewer) {
      return
    }

    // User is logged in, we auto-accept cookies
    saveCookieChoice(true)

    const { id, name, email, avatarUrl, personalAccount, createdAt } =
      data.viewer

    // Sentry
    setSentryUser({
      id,
      username: personalAccount?.slug,
      email,
    })

    // The backend still creates users in rudderstack without the `user_` prefix, so we
    // need to remove it here as well to avoid duplicate users. Ultimately, we should
    // refer to users with the prefixed ID everywhere. This is a fix in the meantime.
    const rudderStackId = id.replace(/^user_/, '')

    // RudderStack
    if (rudder?.getUserId?.() !== rudderStackId) {
      rudder?.identify(
        rudderStackId,
        {
          name,
          email,
          createdAt,
          username: personalAccount?.slug,
          avatar: avatarUrl ?? undefined,
          theme: document.documentElement.className,
        },
        {},
        () => {},
      )
    }
  }, [data?.viewer])

  // Errors
  useEffect(() => {
    if (pause || fetching) {
      return
    }

    if (error?.graphQLErrors) {
      if (isSignedIn) {
        logout().then(() =>
          toasty?.error({
            title: 'Unable to load session',
            text: 'Please, try to sign in again. If the problem persists contact support',
            button: {
              variant: 'secondary',
              children: 'Contact',
              onClick: () => openInNewTab(`mailto:${contactEmail}`),
            },
          }),
        )
      }
    }
  }, [error, fetching, isSignedIn, pause, logout])

  // Sign up Tracking
  useEffect(() => {
    if (
      isProduction &&
      data?.viewer &&
      !localStorage.getItem(signUpTrackedKey)
    ) {
      if (
        differenceInMinutes(new Date(data.viewer.createdAt), Date.now()) < 1
      ) {
        localStorage.setItem(signUpTrackedKey, 'true')
        // linkedin
        window?.lintrk?.('track', { conversion_id: 15155281 })
        try {
          // gtag
          // @ts-ignore
          gtag?.('event', 'conversion', {
            send_to: 'AW-11163587136/XPxFCNS29NwYEMCkm8sp',
          })
        } catch (e) {
          // noop
        }
      }
    }
  }, [data?.viewer])

  return <>{children}</>
}

export default LoadUser
